import React from 'react';
const { useState, useEffect } = React;
import { inputSt, labelSt, cardSt, mutedSt, badgeSt, warnSt, fmtDate } from '../diagnostic/shared.jsx';
import { CONTRIBUTIONS, TIME_LIMITS as TL, TIME_MIN_ENTRIES, parseMinutes, fmtMinutes, weekStartOf, addDays, ownerLabel } from '../../ownerSpaceConfig.js';
import { isValidTimeZone } from '../../coachingTime.js';
import { ownerCall, Notice, chipSt, todayIso } from './shared.jsx';
import { LoadingState, ErrorState, EmptyState } from '../ui/States.jsx';

// Mi tiempo: how I spend my time and what it is for. Every number comes from
// the server (ownerTime.ts). Real time is only what I register by hand; the
// calendar and the estimates are planned time. Effort, action and observed
// result stay apart, and there is no score of any kind.

const browserTz = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; } };
const loadTz = () => { try { const v = localStorage.getItem('oike_myday_tz'); return isValidTimeZone(v) ? v : browserTz(); } catch { return browserTz(); } };
const smallBtn = { fontSize: 11, padding: '2px 8px' };
const GROUPS = [
  ['direct', 'Contribuyen directamente'], ['indirect', 'Contribuyen indirectamente'], ['maintenance', 'Mantenimiento necesario'],
  ['linked_unclassified', 'Ligadas a un objetivo, sin clasificar'], ['unlinked', 'Sin vincular: para revisar'],
];
const weekday = (iso) => { try { return new Date(`${iso}T12:00:00Z`).toLocaleDateString('es', { weekday: 'short', day: 'numeric', timeZone: 'UTC' }); } catch { return iso; } };

function Bar({ value, max }) {
  const pct = max ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return <span aria-hidden="true" style={{ display: 'block', height: 8, width: `${pct}%`, borderRadius: 4, background: 'var(--globant-green)', opacity: 0.7, verticalAlign: 'middle' }} />;
}

/** Goals, category, estimate and kind of contribution of one task: always confirmed by me. */
function TaskLinks({ task, goals, categories, onSaved, onCancel }) {
  const [v, setV] = useState({ goalIds: task.goalIds || [], category: task.category || '', contribution: task.contribution || '', estimate: task.estimatedMinutes ? fmtMinutes(task.estimatedMinutes) : '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const toggle = (id) => setV(x => ({ ...x, goalIds: x.goalIds.includes(id) ? x.goalIds.filter(g => g !== id) : [...x.goalIds, id].slice(0, TL.goalsPerTask) }));
  const save = async () => {
    const est = v.estimate.trim() ? parseMinutes(v.estimate) : null;
    if (v.estimate.trim() && !est) { setError('La estimación no es una duración (usa 1:30, 90 o 1h 30m).'); return; }
    setBusy(true); setError('');
    try { await ownerCall({ action: 'saveTaskLinks', id: task.id, goalIds: v.goalIds, category: v.category, contribution: v.contribution || null, estimatedMinutes: est }); onSaved(); } catch (e) { setError(e.message); setBusy(false); }
  };
  const active = goals.filter(g => (g.status || 'active') === 'active' || v.goalIds.includes(g.id));
  return (
    <div style={{ border: '1px solid var(--globant-border)', borderRadius: 8, padding: 10, margin: '6px 0', display: 'grid', gap: 8 }}>
      <div><span style={labelSt}>Objetivos a los que sirve (hasta {TL.goalsPerTask})</span>
        {!active.length ? <span style={mutedSt}>No tienes objetivos activos.</span>
          : <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{active.map(g => <button type="button" key={g.id} style={chipSt(v.goalIds.includes(g.id))} aria-pressed={v.goalIds.includes(g.id)} onClick={() => toggle(g.id)}>{g.title}</button>)}</div>}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <label style={{ flex: '1 1 160px' }}><span style={labelSt}>Área</span>
          <select style={inputSt} value={v.category} onChange={e => setV({ ...v, category: e.target.value })}>
            <option value="">Sin área</option>
            {categories.filter(c => !c.archived || c.key === v.category).map(c => <option key={c.key} value={c.key}>{c.name}</option>)}
          </select></label>
        <label style={{ flex: '1 1 200px' }}><span style={labelSt}>Cómo contribuye</span>
          <select style={inputSt} value={v.contribution} onChange={e => setV({ ...v, contribution: e.target.value })}>
            <option value="">Sin clasificar</option>
            {CONTRIBUTIONS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select></label>
        <label style={{ flex: '0 1 140px' }}><span style={labelSt}>Estimación</span>
          <input style={inputSt} value={v.estimate} placeholder="1:30" onChange={e => setV({ ...v, estimate: e.target.value })} /></label>
      </div>
      <div style={mutedSt}>Estar ligada a un objetivo no dice que te acercó a él: eso lo dicen los avances que anotas en el objetivo.</div>
      {error && <div role="alert" style={{ color: '#9A6212', fontSize: 12 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 6 }}>
        <button className="action-btn btn-primary" disabled={busy} onClick={save}>{busy ? 'Guardando…' : 'Guardar'}</button>
        <button className="action-btn" onClick={onCancel}>Cancelar</button>
      </div>
    </div>
  );
}

/** Real time, registered by hand. With a task, its goals and category come from the task. */
function EntryForm({ entry, tasks, goals, categories, weekStart, onSaved, onCancel }) {
  const [v, setV] = useState(() => ({
    date: entry?.date || (todayIso() >= weekStart && todayIso() <= addDays(weekStart, 6) ? todayIso() : weekStart),
    duration: entry ? fmtMinutes(entry.minutes) : '', taskId: entry?.taskId || '', goalIds: entry?.goalIds || [],
    category: entry?.category || '', contribution: entry?.contribution || '', note: entry?.note || '',
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const minutes = parseMinutes(v.duration);
  const save = async () => {
    if (!minutes) { setError('Escribe la duración (por ejemplo 1:30, 90 o 1h 30m).'); return; }
    if (minutes > TL.minutes) { setError('Un registro llega hasta 24 horas.'); return; }
    setBusy(true); setError('');
    const body = { date: v.date, minutes, note: v.note, ...(v.taskId ? { taskId: v.taskId } : { goalIds: v.goalIds, category: v.category, contribution: v.contribution || null }) };
    try { await ownerCall({ action: 'saveTimeEntry', ...(entry ? { id: entry.id } : {}), entry: body }); onSaved(); } catch (e) { setError(e.message); setBusy(false); }
  };
  const open = tasks.filter(t => t.status !== 'done' || t.id === v.taskId || (t.doneOn && t.doneOn >= weekStart));
  return (
    <div style={{ ...cardSt, display: 'grid', gap: 8 }}>
      <b style={{ fontSize: 14 }}>{entry ? 'Editar registro' : 'Registrar tiempo'}</b>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <label style={{ flex: '0 1 170px' }}><span style={labelSt}>Día</span><input type="date" style={inputSt} value={v.date} onChange={e => setV({ ...v, date: e.target.value })} /></label>
        <label style={{ flex: '0 1 150px' }}><span style={labelSt}>Duración</span><input style={inputSt} value={v.duration} placeholder="1:30" aria-describedby="dur-hint" onChange={e => setV({ ...v, duration: e.target.value })} /></label>
        <label style={{ flex: '1 1 220px' }}><span style={labelSt}>Tarea (opcional)</span>
          <select style={inputSt} value={v.taskId} onChange={e => setV({ ...v, taskId: e.target.value })}>
            <option value="">Sin tarea</option>
            {open.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
          </select></label>
      </div>
      <div id="dur-hint" style={mutedSt}>{minutes ? `= ${fmtMinutes(minutes)}` : 'Acepta 1:30, 90, 1h 30m o 45m.'} Solo cuenta como tiempo real lo que registras aquí.</div>
      {v.taskId ? <div style={mutedSt}>Los objetivos y el área salen de la tarea: cámbialos en la tarea, no aquí.</div> : (
        <div style={{ display: 'grid', gap: 8 }}>
          <div><span style={labelSt}>Objetivos</span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{goals.filter(g => (g.status || 'active') === 'active' || v.goalIds.includes(g.id)).map(g => (
              <button type="button" key={g.id} style={chipSt(v.goalIds.includes(g.id))} aria-pressed={v.goalIds.includes(g.id)} onClick={() => setV(x => ({ ...x, goalIds: x.goalIds.includes(g.id) ? x.goalIds.filter(y => y !== g.id) : [...x.goalIds, g.id].slice(0, TL.goalsPerTask) }))}>{g.title}</button>))}
              {!goals.length && <span style={mutedSt}>Sin objetivos.</span>}</div></div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <label style={{ flex: '1 1 160px' }}><span style={labelSt}>Área</span>
              <select style={inputSt} value={v.category} onChange={e => setV({ ...v, category: e.target.value })}>
                <option value="">Sin área</option>
                {categories.filter(c => !c.archived || c.key === v.category).map(c => <option key={c.key} value={c.key}>{c.name}</option>)}
              </select></label>
            <label style={{ flex: '1 1 200px' }}><span style={labelSt}>Cómo contribuye</span>
              <select style={inputSt} value={v.contribution} onChange={e => setV({ ...v, contribution: e.target.value })}>
                <option value="">Sin clasificar</option>
                {CONTRIBUTIONS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select></label>
          </div>
        </div>
      )}
      <label><span style={labelSt}>Nota (opcional)</span><input style={inputSt} value={v.note} maxLength={TL.note} onChange={e => setV({ ...v, note: e.target.value })} /></label>
      {error && <div role="alert" style={{ color: '#9A6212', fontSize: 12 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 6 }}>
        <button className="action-btn btn-primary" disabled={busy} onClick={save}>{busy ? 'Guardando…' : 'Guardar'}</button>
        <button className="action-btn" onClick={onCancel}>Cancelar</button>
      </div>
    </div>
  );
}

function Categories({ categories, onChanged }) {
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const save = async (id, category) => { setError(''); try { await ownerCall({ action: 'saveCategory', ...(id ? { id } : {}), category }); onChanged(); } catch (e) { setError(e.message); } };
  return (
    <details style={cardSt}>
      <summary style={{ cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>Áreas</summary>
      <p style={mutedSt}>Son tuyas: cámbiales el nombre, agrega las que te sirvan o archiva las que no. Descanso, vínculos y cuidado personal valen por sí mismos: nunca se cuentan como tiempo perdido.</p>
      {categories.map(c => (
        <div key={c.id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '6px 0', opacity: c.archived ? 0.6 : 1 }}>
          <input style={{ ...inputSt, flex: '1 1 180px' }} defaultValue={c.name} maxLength={TL.categoryName} aria-label={`Nombre de la categoría ${c.name}`} onBlur={e => { const n = e.target.value.trim(); if (n && n !== c.name) save(c.id, { ...c, name: n }); }} />
          <label style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center' }}><input type="checkbox" checked={c.maintenance} onChange={e => save(c.id, { ...c, maintenance: e.target.checked })} /> mantenimiento</label>
          <button className="action-btn" style={smallBtn} onClick={() => save(c.id, { ...c, archived: !c.archived })}>{c.archived ? 'Recuperar' : 'Archivar'}</button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <input style={inputSt} value={name} placeholder="Nueva área" maxLength={TL.categoryName} onChange={e => setName(e.target.value)} />
        <button className="action-btn" disabled={!name.trim()} onClick={async () => { await save(null, { name: name.trim() }); setName(''); }}>Agregar</button>
      </div>
      {error && <div role="alert" style={{ color: '#9A6212', fontSize: 12, marginTop: 6 }}>{error}</div>}
    </details>
  );
}

/** The AI proposes links; each one is saved only if I confirm it. */
function Suggestions({ onChanged }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const ask = async () => { setBusy(true); setError(''); try { setState(await ownerCall({ action: 'suggestTaskLinks' })); } catch (e) { setError(e.message); } setBusy(false); };
  const confirm = async (s) => {
    try { await ownerCall({ action: 'saveTaskLinks', id: s.taskId, goalIds: s.goals.map(g => g.id), ...(s.contribution ? { contribution: s.contribution } : {}) }); setState(x => ({ ...x, suggestions: x.suggestions.filter(y => y !== s) })); onChanged(); } catch (e) { setError(e.message); }
  };
  return (
    <div style={cardSt}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <b style={{ fontSize: 14 }}>Vincular tareas con objetivos</b>
        <button className="action-btn" disabled={busy} onClick={ask}>{busy ? 'Pidiendo sugerencias…' : '✨ Pedir sugerencias a la IA'}</button>
      </div>
      <Notice icon="✨" tone="ai">Solo se envían los títulos de tus tareas sin vincular y de tus objetivos activos no excluidos. La IA propone; nada se guarda hasta que lo confirmas.</Notice>
      {error && <div role="alert" style={{ color: '#9A6212', fontSize: 12 }}>{error}</div>}
      {state && !state.suggestions.length && <div style={mutedSt}>{state.reason === 'no_tasks' ? 'No hay tareas sin vincular.' : state.reason === 'no_goals' ? 'No hay objetivos activos para proponer.' : 'No hay más propuestas.'}</div>}
      {state?.suggestions.map((s, i) => (
        <div key={`${s.taskId}-${i}`} style={{ borderLeft: '2px solid var(--globant-border)', paddingLeft: 8, margin: '8px 0', fontSize: 13 }}>
          <b>{s.taskTitle}</b> → {s.goals.length ? s.goals.map(g => g.title).join(', ') : <span style={mutedSt}>ningún objetivo</span>}
          {s.contribution && <span style={{ ...badgeSt(false), marginLeft: 6 }}>{ownerLabel(CONTRIBUTIONS, s.contribution)}</span>}
          {s.why && <div style={mutedSt}>Borrador de la IA: {s.why}</div>}
          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
            {(s.goals.length > 0 || s.contribution) && <button className="action-btn" style={smallBtn} onClick={() => confirm(s)}>Confirmar</button>}
            <button className="action-btn" style={smallBtn} onClick={() => setState(x => ({ ...x, suggestions: x.suggestions.filter(y => y !== s) }))}>Descartar</button>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * El contador en Mi tiempo: los tramos por confirmar (más largos que el límite de revisión, posiblemente olvidados),
 * que no suman al tiempo registrado hasta que los confirmas o corriges, y los ajustes de avisos. Nada se detiene ni
 * se descuenta solo.
 */
function TimerPanel({ onChanged }) {
  const [list, setList] = useState(null);
  const [settings, setSettings] = useState(null);
  const [edit, setEdit] = useState({});
  const [error, setError] = useState('');
  const [schema, setSchema] = useState(true);
  const load = () => Promise.all([ownerCall({ action: 'timerReview' }), ownerCall({ action: 'timerState', timeZone: loadTz() })])
    .then(([r, st]) => { setList(r.stretches); setSettings(st.settings); setSchema(true); })
    .catch(e => { if (e.code === 'schema_pending') setSchema(false); else setError(e.message); });
  useEffect(() => { load(); }, []);
  const confirm = async (x) => {
    setError('');
    const raw = edit[x.id];
    const minutes = raw === undefined || raw === '' ? undefined : parseMinutes(raw);
    if (raw && !minutes) { setError('Escribe la duración como 45, 1:30 o 1h 30m.'); return; }
    try { await ownerCall({ action: 'confirmStretch', id: x.id, minutes }); setEdit({ ...edit, [x.id]: undefined }); await load(); onChanged(); } catch (e) { setError(e.message); }
  };
  const saveSettings = async () => {
    setError('');
    try { const r = await ownerCall({ action: 'timerSettings', ...settings }); setSettings(r.settings); } catch (e) { setError(e.message); }
  };
  if (!schema) return <div style={{ ...warnSt, padding: 10 }}>El contador necesita cinco campos nuevos en «Personal Time Entries» (Started At, Ended At, Entry Status, Entry Source y End Reason). El pedido para Omni está en docs/mi-espacio/configuracion.md. Lo demás de Mi tiempo funciona igual.</div>;
  if (!list) return null;
  return (
    <div style={cardSt}>
      <b style={{ fontSize: 14 }}>Contador</b>
      <div style={mutedSt}>Un tramo de más de {fmtMinutes(settings?.reviewMinutes || 240)} queda <b>por confirmar</b>: no suma al tiempo registrado hasta que lo confirmes o corrijas. Nada se detiene ni se descuenta solo.</div>
      {!list.length ? <div style={{ ...mutedSt, marginTop: 6 }}>No hay tramos por confirmar.</div> : list.map(x => (
        <div key={x.id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '6px 0', borderBottom: '1px solid var(--globant-border)', fontSize: 13 }}>
          <span style={{ flex: '1 1 200px', minWidth: 0 }}>{x.taskTitle || x.note || 'Tramo sin tarea'} <span style={mutedSt}>· {fmtDate(x.date)}{x.startedAt ? ` · desde ${new Date(x.startedAt).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}` : ''}</span></span>
          <span style={badgeSt(false)}>{fmtMinutes(x.minutes)} por confirmar</span>
          <input style={{ ...inputSt, width: 110 }} placeholder="Corregir (1:30)" value={edit[x.id] ?? ''} onChange={e => setEdit({ ...edit, [x.id]: e.target.value })} aria-label="Duración corregida" />
          <button className="action-btn btn-primary" style={smallBtn} onClick={() => confirm(x)}>{edit[x.id] ? 'Corregir y confirmar' : 'Confirmar'}</button>
        </div>
      ))}
      {settings && (
        <details style={{ marginTop: 8 }}>
          <summary style={{ cursor: 'pointer', fontSize: 12 }}>Ajustes del contador</summary>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginTop: 6, fontSize: 12 }}>
            <label>Aviso discreto cada <input type="number" min={15} max={480} style={{ ...inputSt, width: 70 }} value={settings.noticeMinutes} onChange={e => setSettings({ ...settings, noticeMinutes: Number(e.target.value) })} /> min</label>
            <label>Por confirmar si el tramo supera <input type="number" min={60} max={1440} style={{ ...inputSt, width: 80 }} value={settings.reviewMinutes} onChange={e => setSettings({ ...settings, reviewMinutes: Number(e.target.value) })} /> min</label>
            <button className="action-btn" style={smallBtn} onClick={saveSettings}>Guardar ajustes</button>
          </div>
          <div style={{ ...mutedSt, marginTop: 4 }}>El aviso solo aparece en la barra del contador; no interrumpe ni detiene nada. Cambiar de pestaña o trabajar fuera de Oike no genera avisos.</div>
        </details>
      )}
      {error && <div role="alert" style={{ ...warnSt, padding: 8, marginTop: 6 }}>{error}</div>}
    </div>
  );
}

export default function MyTime() {
  const [weekStart, setWeekStart] = useState(weekStartOf(todayIso()));
  const [w, setW] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // 'new' | entry
  const [linking, setLinking] = useState(null); // task id
  const load = () => { setError(''); ownerCall({ action: 'timeWeek', weekStart, timeZone: loadTz() }).then(setW).catch(e => setError(e.message)); };
  useEffect(() => { setW(null); load(); }, [weekStart]); // eslint-disable-line react-hooks/exhaustive-deps
  const remove = async (e) => { if (!window.confirm('¿Borrar este registro de tiempo?')) return; try { await ownerCall({ action: 'deleteTimeEntry', id: e.id }); load(); } catch (err) { setError(err.message); } };

  const nav = (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
      <button className="action-btn" onClick={() => setWeekStart(addDays(weekStart, -7))} aria-label="Semana anterior">←</button>
      <b>Semana del {fmtDate(weekStart)} al {fmtDate(addDays(weekStart, 6))}</b>
      <button className="action-btn" onClick={() => setWeekStart(addDays(weekStart, 7))} aria-label="Semana siguiente">→</button>
      {weekStart !== weekStartOf(todayIso()) && <button className="action-btn" onClick={() => setWeekStart(weekStartOf(todayIso()))}>Esta semana</button>}
      <span style={{ flex: 1 }} />
      <button className="action-btn btn-primary" onClick={() => setEditing('new')}>⏱ Registrar tiempo</button>
    </div>
  );
  if (error && !w) return <div>{nav}<ErrorState message={error} onRetry={load} /></div>;
  if (!w) return <div>{nav}<LoadingState text="Calculando tu semana…" /></div>;

  const taskById = new Map(w.tasks.map(t => [t.id, t]));
  const goalById = new Map(w.goals.map(g => [g.id, g]));
  const catName = (k) => w.categories.find(c => c.key === k)?.name || k;
  const maxCat = Math.max(0, ...w.real.byCategory.map(c => c.minutes));
  const linkEditor = (id) => linking === id && taskById.get(id) && <TaskLinks task={taskById.get(id)} goals={w.goals} categories={w.categories} onCancel={() => setLinking(null)} onSaved={() => { setLinking(null); load(); }} />;

  return (
    <div>
      {nav}
      <Notice icon="🔒" tone="private">Solo tuyo. El tiempo real es solo lo que registras a mano o con el contador (y confirmas); el calendario y las estimaciones son tiempo planificado y nunca cuentan como real. Sin puntajes: descansar no es tiempo perdido.</Notice>
      {error && <ErrorState message={error} onRetry={load} />}
      {editing && <EntryForm key={editing === 'new' ? 'new' : editing.id} entry={editing === 'new' ? null : editing} tasks={w.tasks} goals={w.goals} categories={w.categories} weekStart={weekStart} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
        <div style={{ ...cardSt, marginBottom: 0 }}>
          <span style={labelSt}>Tiempo registrado</span>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{fmtMinutes(w.real.minutes)}</div>
          <div style={mutedSt}>{w.real.entries} registro{w.real.entries === 1 ? '' : 's'} · {7 - w.gaps.daysWithoutRecords.length} de 7 días con información</div>
          {w.unclassified?.entries > 0 && <div style={{ ...mutedSt, color: '#9A6212' }}>{fmtMinutes(w.unclassified.minutes)} sin clasificar (sin tarea ni área).</div>}
        </div>
        {w.pending?.minutes > 0 && (
          <div style={{ ...cardSt, marginBottom: 0, borderColor: 'rgba(245,158,11,0.5)' }}>
            <span style={labelSt}>Por confirmar</span>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{fmtMinutes(w.pending.minutes)}</div>
            <div style={mutedSt}>{w.pending.entries.length} tramo{w.pending.entries.length === 1 ? '' : 's'} posiblemente olvidado{w.pending.entries.length === 1 ? '' : 's'}: no se suman al tiempo registrado hasta que los confirmes.</div>
          </div>
        )}
        <div style={{ ...cardSt, marginBottom: 0 }}>
          <span style={labelSt}>Tiempo planificado</span>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{fmtMinutes(w.planned.minutes)}</div>
          <div style={mutedSt}>{fmtMinutes(w.planned.fromTasks)} de tareas y estimaciones · {w.planned.calendarConnected ? `${fmtMinutes(w.planned.fromCalendar)} de otros eventos del calendario` : 'calendario no conectado'}. Una tarea y su evento cuentan una vez.</div>
        </div>
      </div>
      <TimerPanel onChanged={load} />
      {w.limited && <div style={{ ...warnSt, padding: 10 }}>Hay pocos registros esta semana ({w.real.entries}; con menos de {TIME_MIN_ENTRIES} el análisis es limitado). Los días sin registros son <b>sin información</b>, no cero.</div>}

      <div style={cardSt}>
        <b style={{ fontSize: 14 }}>Por área</b>
        {!w.real.byCategory.length ? <div style={{ ...mutedSt, marginTop: 6 }}>Sin tiempo registrado esta semana.</div> : w.real.byCategory.map(c => (
          <div key={c.key || 'none'} style={{ display: 'grid', gridTemplateColumns: 'minmax(110px, 1fr) 2fr auto', gap: 8, alignItems: 'center', fontSize: 13, margin: '6px 0' }}>
            <span>{c.name}{c.maintenance && <span style={{ ...badgeSt(false), marginLeft: 6 }}>mantenimiento</span>}</span>
            <span style={{ display: 'block' }}><Bar value={c.minutes} max={maxCat} /></span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMinutes(c.minutes)}</span>
          </div>
        ))}
      </div>

      <div style={cardSt}>
        <b style={{ fontSize: 14 }}>Por objetivo</b>
        <p style={{ ...mutedSt, margin: '4px 0 8px' }}>Tres cosas distintas: el <b>esfuerzo</b> (tiempo registrado), la <b>acción</b> (tareas hechas) y el <b>resultado observado</b> (los avances que anotaste en el objetivo). Una no prueba la otra.</p>
        {!w.byGoal.length ? <EmptyState title="Ningún objetivo con movimiento esta semana">Vincula tus tareas con tus objetivos para verlo aquí.</EmptyState> : w.byGoal.map(g => (
          <div key={g.goalId} style={{ borderTop: '1px solid var(--globant-border)', padding: '8px 0', fontSize: 13 }}>
            <b>{g.title}</b>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, marginTop: 4 }}>
              <div><span style={labelSt}>Esfuerzo</span>{g.effortMinutes ? fmtMinutes(g.effortMinutes) : <span style={mutedSt}>sin tiempo registrado</span>}</div>
              <div><span style={labelSt}>Acción</span>{g.actions.length ? g.actions.map(a => <div key={a.id}>✓ {a.title}{a.contribution && <span style={mutedSt}> · {ownerLabel(CONTRIBUTIONS, a.contribution).toLowerCase()}</span>}</div>) : <span style={mutedSt}>ninguna tarea hecha</span>}</div>
              <div><span style={labelSt}>Resultado observado</span>{g.observed.length ? g.observed.map(o => <div key={o.id}>{fmtDate(o.date)} · {o.text}</div>) : <span style={mutedSt}>ningún avance anotado</span>}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={cardSt}>
        <b style={{ fontSize: 14 }}>Tareas de la semana según cómo contribuyen</b>
        <p style={{ ...mutedSt, margin: '4px 0 8px' }}>Según como tú las clasificaste. Las administrativas y de cuidado que sostienen lo demás son mantenimiento necesario.</p>
        {GROUPS.map(([k, label]) => w.contributions[k]?.length ? (
          <div key={k} style={{ margin: '8px 0' }}>
            <span style={labelSt}>{label} · {w.contributions[k].length}</span>
            {w.contributions[k].map(t => (
              <div key={t.id} style={{ fontSize: 13, margin: '4px 0' }}>
                <span>{t.status === 'done' ? '✓ ' : ''}{t.title}</span>
                {t.goalIds?.length > 0 && <span style={mutedSt}> · {t.goalIds.map(id => goalById.get(id)?.title).filter(Boolean).join(', ')}</span>}
                {t.category && <span style={mutedSt}> · {catName(t.category)}</span>}
                <button className="action-btn" style={{ ...smallBtn, marginLeft: 8 }} onClick={() => setLinking(linking === t.id ? null : t.id)}>{k === 'unlinked' ? 'Vincular' : 'Editar vínculo'}</button>
                {linkEditor(t.id)}
              </div>
            ))}
          </div>
        ) : null)}
        {!GROUPS.some(([k]) => w.contributions[k]?.length) && <div style={mutedSt}>No hay tareas con fecha en esta semana.</div>}
      </div>

      {(w.gaps.daysWithoutRecords.length > 0 || w.gaps.doneWithoutTime.length > 0) && (
        <div style={cardSt}>
          <b style={{ fontSize: 14 }}>Sin información</b>
          {w.gaps.daysWithoutRecords.length > 0 && <div style={{ fontSize: 13, marginTop: 6 }}>Días sin registros: {w.gaps.daysWithoutRecords.map(weekday).join(', ')}. No sabemos cómo fueron: no se cuentan como cero.</div>}
          {w.gaps.doneWithoutTime.length > 0 && <div style={{ fontSize: 13, marginTop: 6 }}>Tareas hechas sin tiempo registrado: {w.gaps.doneWithoutTime.map(t => t.title).join(', ')}.</div>}
        </div>
      )}

      <div style={cardSt}>
        <b style={{ fontSize: 14 }}>Registros de la semana</b>
        {!w.entries.length ? <div style={{ ...mutedSt, marginTop: 6 }}>Todavía no registraste tiempo esta semana.</div> : (
          <ul style={{ listStyle: 'none', padding: 0, margin: '6px 0 0' }}>
            {w.entries.map(e => {
              const t = e.taskId ? taskById.get(e.taskId) : null;
              return (
                <li key={e.id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 13, padding: '6px 0', borderTop: '1px solid var(--globant-border)' }}>
                  <span style={{ minWidth: 70 }}>{weekday(e.date)}</span>
                  <b style={{ minWidth: 70, fontVariantNumeric: 'tabular-nums' }}>{fmtMinutes(e.minutes)}</b>
                  <span style={{ flex: 1, minWidth: 140 }}>{t ? t.title : e.note || <span style={mutedSt}>sin tarea</span>}{t && e.note ? <span style={mutedSt}> · {e.note}</span> : null}</span>
                  <button className="action-btn" style={smallBtn} onClick={() => setEditing(e)}>Editar</button>
                  <button className="action-btn btn-danger" style={smallBtn} onClick={() => remove(e)}>Borrar</button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <Suggestions onChanged={load} />
      <Categories categories={w.categories} onChanged={load} />
    </div>
  );
}
